// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
// Global modules
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CreateAxiosDefaults } from 'axios';
import omit from 'lodash.omit';

// Models
import { AuthEndpoints, ISignInAuthorizedResponse } from '@/models/auth.model';

// Utils/Helpers
import {
  ADDRESSES_API_URL,
  API_URL,
  AUTHORIZATION_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from '@/constants/common';
import { COOKIE_KEYS } from '@/constants/cookies';
import { AuthAPI } from '@/api';
import appCookiesStorage from './appCookies';
import { isSessionError } from './formatApiError';
import { forceSignOut, setAuthToken } from './session';

export const isUndefined = (value: string | undefined) => {
  return !value || value === 'undefined';
};

/** Запросы самой авторизации не должны уходить в цикл «401 -> рефреш -> 401» */
const AUTH_ENDPOINTS = Object.values(AuthEndpoints) as string[];

const isAuthRequest = (url?: string) => !!url && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

let refreshSessionRequest: Promise<AxiosResponse<ISignInAuthorizedResponse>> | null;

const defaultConfigs: AxiosRequestConfig = {
  baseURL: API_URL,
};

interface RefreshTokenResponse {
  refresh_token: string;
}

interface HttpClientRequestConfig extends AxiosRequestConfig {
  isRetry?: boolean;
}

interface HttpClientError extends AxiosError {
  config: HttpClientRequestConfig;
}

class HttpClient<TClient extends AxiosInstance> {
  private readonly client;
  readonly refreshRequest: Promise<AxiosResponse<RefreshTokenResponse>> | null;
  readonly refreshToken;

  get;
  post;
  put;
  patch;
  delete;

  constructor(client: TClient) {
    this.client = client;
    this.refreshRequest = null;
    this.refreshToken = appCookiesStorage.getItem(COOKIE_KEYS.REFRESH_TOKEN);

    const requestInterceptor = async (config: AxiosRequestConfig) => {
      const isUrlSearchParams = config.params instanceof URLSearchParams;
      const authToken =
        appCookiesStorage.getItem(AUTHORIZATION_TOKEN_STORAGE_KEY) ??
        (isUrlSearchParams
          ? config.params.get(AUTHORIZATION_TOKEN_STORAGE_KEY)
          : (config?.params?.[AUTHORIZATION_TOKEN_STORAGE_KEY] as string)) ??
        '';

      if (isUrlSearchParams) {
        config.params.delete(AUTHORIZATION_TOKEN_STORAGE_KEY);
      }

      const updated = {
        ...config,
        headers: { ...config.headers },
        params: isUrlSearchParams ? config.params ?? {} : omit(config.params ?? {}, [AUTHORIZATION_TOKEN_STORAGE_KEY]),
      };

      if (authToken) {
        updated.headers.Authorization = `Bearer ${authToken}`;
      }

      return updated;
    };

    const errorResponseInterceptor = async (error: HttpClientError & { config: { isRetry: boolean } }) => {
      const config = error?.config ?? ({} as HttpClientRequestConfig);

      if (!isSessionError(error) || config.isRetry || isAuthRequest(config.url)) {
        throw error;
      }

      const refreshToken =
        appCookiesStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) ?? config.params?.[REFRESH_TOKEN_STORAGE_KEY] ?? '';

      /** Обновить сессию нечем — сбрасываем её и уводим на логин */
      if (isUndefined(refreshToken)) {
        forceSignOut();
        throw error;
      }

      try {
        refreshSessionRequest ??= AuthAPI.refreshToken({ refreshToken });

        const { data: session } = await refreshSessionRequest;

        refreshSessionRequest = null;

        const updatedJwtToken = session?.idToken;
        if (!updatedJwtToken) {
          throw error;
        }

        setAuthToken(updatedJwtToken);

        config.isRetry = true;
        config.headers = { ...config.headers, Authorization: `Bearer ${updatedJwtToken}` };

        return await this.client(config);
      } catch (e) {
        refreshSessionRequest = null;

        /** Сеть отвалилась — сессия может быть живой, разлогинивать не за что */
        if (axios.isAxiosError(e) && !e.response) {
          throw e;
        }

        forceSignOut();
        throw e;
      }
    };

    /** Adding the request interceptors */
    this.client.interceptors.request.use(requestInterceptor);

    /** Adding the response interceptors */
    this.client.interceptors.response.use((r: AxiosResponse) => r, errorResponseInterceptor);

    this.get = this.client.get.bind(this);
    this.post = this.client.post.bind(this);
    this.put = this.client.put.bind(this);
    this.patch = this.client.patch.bind(this);
    this.delete = this.client.delete.bind(this);
  }
}

const defaultClient = axios.create(defaultConfigs as CreateAxiosDefaults);

export const isHttpClientError = axios.isAxiosError;

export default new HttpClient(defaultClient);

// Addresses client instance
const addressesClient = axios.create({
  baseURL: ADDRESSES_API_URL,
} as CreateAxiosDefaults);

export const addressesHttpClient = new HttpClient(addressesClient);

export type { AxiosError as HttpClientError };
export type ApiResponse<Response> = Promise<AxiosResponse<Response>>;
